// frontend/src/components/themed/ThemedInput.js - Professional themed input component
import React, { forwardRef } from 'react';
import { useTheme } from '../../contexts/ThemeContext';

const ThemedInput = forwardRef(({ 
  type = 'text',
  label,
  error,
  helperText,
  disabled = false,
  fullWidth = true,
  size = 'md',
  icon: Icon,
  className = '',
  containerClassName = '',
  ...props 
}, ref) => {
  const { components, text, states, cn } = useTheme();

  // Size classes
  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-3 py-2',
    lg: 'px-4 py-3 text-lg',
  };

  // Build input classes
  const inputClasses = cn(
    'block rounded-md border transition-colors',
    fullWidth ? 'w-full' : '',
    sizeClasses[size],
    Icon ? 'pl-10' : '',
    disabled ? components.input.disabled : components.input.base,
    error ? components.input.error : '',
    !disabled && !error ? components.input.focus : '',
    states.focus.outline,
    className
  );

  return (
    <div className={cn(fullWidth ? 'w-full' : '', containerClassName)}>
      {label && (
        <label className={`block text-sm font-medium mb-1 ${text.secondary}`}>
          {label}
        </label>
      )}
      
      <div className="relative">
        {Icon && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Icon className={`h-5 w-5 ${error ? 'text-red-500' : text.muted}`} />
          </div>
        )}
        
        <input
          ref={ref}
          type={type}
          disabled={disabled}
          className={inputClasses}
          {...props}
        />
      </div>
      
      {(error || helperText) && (
        <p className={`mt-1 text-sm ${error ? 'text-red-600' : text.muted}`}>
          {error || helperText}
        </p>
      )}
    </div>
  );
});

ThemedInput.displayName = 'ThemedInput';

export default ThemedInput;